import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, Navigation2, Route as RouteIcon } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { FaqSection } from "@/components/FaqSection";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findTrip, TRIPS, tripSlug, TRIP_PATH } from "@/lib/trips";
import { findCity, CITY_PATH } from "@/lib/cities";
import { tripFaq } from "@/lib/faq";
import { THEMATIC_PAGES } from "@/lib/thematic";

export default function TripPage() {
  const { slug } = useParams();
  const trip = findTrip(slug);
  const from = trip ? findCity(trip.from) : null;
  const to = trip ? findCity(trip.to) : null;
  const ok = trip && from && to;
  const faqItems = ok ? tripFaq(from, to) : [];

  useSeo(
    ok ? `Itinéraire voiture sans permis ${from.name} → ${to.name} (45 km/h) | vspo` : "vspo",
    ok
      ? `Trajet en voiture sans permis (VSP) de ${from.name} à ${to.name} : itinéraire 45 km/h évitant autoroutes et voies rapides, navigation vocale et trafic en temps réel avec vspo.`
      : "",
    ok ? `${TRIP_PATH}/${tripSlug(trip)}` : undefined,
    ok ? ogImageUrl({ title: `${from.name} → ${to.name}`, subtitle: "Itinéraire VSP" }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "trip-jsonld",
    ok
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: `${from.name} → ${to.name}`, item: `${origin}${TRIP_PATH}/${tripSlug(trip)}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  if (!ok) return <Navigate to="/" replace />;

  const others = TRIPS.filter((t) => tripSlug(t) !== tripSlug(trip)).slice(0, 10);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Car className="h-5 w-5" /></span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link to="/" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground" data-testid="trip-open-app">
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <span className="text-foreground">{from.name} → {to.name}</span>
        </nav>

        <div className="mb-2 flex items-center gap-2">
          <RouteIcon className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Itinéraire VSP</span>
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="trip-title">
          {from.name} → {to.name} en voiture sans permis
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          Besoin d'un itinéraire en <strong>voiture sans permis</strong> de <strong>{from.name}</strong> à{" "}
          <strong>{to.name}</strong> ? vspo calcule une liaison adaptée aux voiturettes bridées à{" "}
          <strong>45 km/h</strong>, en évitant les autoroutes et les voies rapides des deux secteurs.
        </p>

        <section className="mt-8">
          <h2 className="font-display text-xl font-bold text-foreground">Un trajet réellement adapté aux VSP</h2>
          <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
            <p>
              Les GPS classiques proposent souvent des axes interdits aux VSP ({from.road} ou {to.road}, par exemple).
              vspo évite ces voies et reste sur le réseau ordinaire, à la vitesse réelle d'une voiturette.
            </p>
            <p>
              Vous profitez de la navigation vocale en français, du trafic en temps réel et, pour les modèles
              électriques, d'un éventuel arrêt recharge sur le trajet.
            </p>
          </div>
        </section>

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">Calculez l'itinéraire {from.name} → {to.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ouvrez vspo, entrez {from.name} comme départ et {to.name} comme arrivée.</p>
          <Link to="/" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="trip-cta">
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        <section className="mt-8">
          <h2 className="font-display text-base font-bold text-foreground">Pages liées</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to={`${CITY_PATH}/${from.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary hover:bg-muted" data-testid={`trip-city-${from.slug}`}>GPS VSP {from.prep}</Link>
            <Link to={`${CITY_PATH}/${to.slug}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary hover:bg-muted" data-testid={`trip-city-${to.slug}`}>GPS VSP {to.prep}</Link>
            {THEMATIC_PAGES.map((t) => (
              <Link key={t.slug} to={t.path} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-primary hover:bg-muted">{t.kicker}</Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display text-base font-bold text-foreground">Autres itinéraires populaires</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((t) => {
              const f = findCity(t.from), d = findCity(t.to);
              if (!f || !d) return null;
              return (
                <Link key={tripSlug(t)} to={`${TRIP_PATH}/${tripSlug(t)}`} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary" data-testid={`trip-link-${tripSlug(t)}`}>
                  {f.name} → {d.name}
                </Link>
              );
            })}
          </div>
        </section>

        <FaqSection items={faqItems} />
      </main>

      <SeoFooter />
    </div>
  );
}
